import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiError, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, PrimaryButton, GhostButton, Card, Modal, Input, Select, Textarea } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, Plus, Trash2, CreditCard, Upload, Send, UserPlus, MapPin,
  CheckCircle2, Award, Users, Truck, ClipboardList, Copy,
} from "lucide-react";
import { toast } from "sonner";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const [data, setData] = useState(null);
  const isOps = hasRole("ops", "admin");
  const isFinance = hasRole("finance", "admin");

  const load = useCallback(() => api.get(`/bookings/${id}`).then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); navigate("/app/bookings"); }), [id, navigate]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Spinner />;
  const { booking, participants, consents, assignments, attendance, performance, certificates, payments } = data;

  const consentMap = Object.fromEntries(consents.map((c) => [c.participant_id, c]));
  const attMap = Object.fromEntries(attendance.map((a) => [a.participant_id, a.status]));

  // ---------------- payment
  const payNow = async () => {
    try {
      const { data: order } = await api.post(`/bookings/${id}/payment/order`);
      if (order.simulated || !order.key_id) {
        await api.post(`/bookings/${id}/payment/verify`, { razorpay_order_id: order.order_id, razorpay_payment_id: "pay_sim", razorpay_signature: "sim" });
        toast.success("Payment completed (test mode)"); load(); return;
      }
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); return; }
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency,
        name: "SPORA1", description: order.booking_reference, order_id: order.order_id,
        prefill: { name: user?.name, email: user?.email },
        theme: { color: "#FF6B00" },
        handler: async (res) => {
          try {
            await api.post(`/bookings/${id}/payment/verify`, {
              razorpay_order_id: res.razorpay_order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature });
            toast.success("Payment successful"); load();
          } catch (e) { toast.error(apiError(e)); }
        },
      });
      rzp.open();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setBookingStatus = async (status) => {
    try { await api.patch(`/bookings/${id}/status`, { status }); toast.success(`Status: ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="animate-fadeUp">
      <button onClick={() => navigate("/app/bookings")} className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to bookings
      </button>
      <PageHeader title={booking.title} subtitle={`${booking.reference} · ${booking.institution_name}`}
        action={<div className="flex items-center gap-2"><StatusBadge status={booking.payment_status} /><StatusBadge status={booking.status} /></div>} />

      <Tabs defaultValue="overview">
        <TabsList className="mb-6 flex w-full flex-wrap justify-start gap-1 bg-card">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="participants" data-testid="tab-participants">Participants</TabsTrigger>
          <TabsTrigger value="logistics" data-testid="tab-logistics">Logistics</TabsTrigger>
          <TabsTrigger value="eventday" data-testid="tab-eventday">Event Day</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="tab-certificates">Certificates</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview">
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <h3 className="mb-3 font-display text-lg font-semibold">Booking summary</h3>
              <Detail label="Type" value={booking.type} />
              <Detail label="Sport" value={booking.sport} />
              <Detail label="Amount" value={formatINR(booking.amount)} />
              <Detail label="Paid" value={formatINR(booking.amount_paid)} />
              <Detail label="Outstanding" value={formatINR(booking.amount - booking.amount_paid)} />
              <Detail label="Participants" value={participants.length} />
              {isOps && (
                <div className="mt-4">
                  <p className="mb-2 font-mono text-xs uppercase text-muted-foreground">Update status</p>
                  <div className="flex flex-wrap gap-2">
                    {["confirmed", "in_progress", "completed", "cancelled"].map((s) => (
                      <GhostButton key={s} data-testid={`set-status-${s}`} onClick={() => setBookingStatus(s)} className="py-2 capitalize">{s.replace("_", " ")}</GhostButton>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Card>
              <div className="mb-3 flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-semibold">Payment</h3></div>
              {booking.payment_status === "paid" ? (
                <div className="flex flex-col items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-center">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                  <p className="font-semibold text-emerald-300">Fully paid</p>
                  <p className="text-sm text-muted-foreground">{formatINR(booking.amount_paid)} received</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">Amount due</p>
                  <p className="font-display text-3xl font-bold text-amber-400">{formatINR(booking.amount - booking.amount_paid)}</p>
                  <PrimaryButton data-testid="razorpay-checkout-btn" onClick={payNow} className="mt-4 w-full"><CreditCard className="h-4 w-4" /> Pay now</PrimaryButton>
                  <p className="mt-2 text-center text-xs text-muted-foreground">Secured by Razorpay (test mode)</p>
                </>
              )}
              {payments.length > 0 && (
                <div className="mt-4 space-y-2 border-t border-border pt-3">
                  <p className="font-mono text-xs uppercase text-muted-foreground">Payment history</p>
                  {payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm">
                      <span className="font-mono text-xs text-muted-foreground">{p.order_id.slice(0, 16)}</span>
                      <span className="flex items-center gap-2"><span className="font-mono">{formatINR(p.amount)}</span><StatusBadge status={p.status} /></span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* PARTICIPANTS */}
        <TabsContent value="participants">
          <ParticipantsTab bookingId={id} participants={participants} consentMap={consentMap} canEdit={!hasRole("finance")} reload={load} />
        </TabsContent>

        {/* LOGISTICS */}
        <TabsContent value="logistics">
          <LogisticsTab bookingId={id} booking={booking} assignments={assignments} isOps={isOps} reload={load} />
        </TabsContent>

        {/* EVENT DAY */}
        <TabsContent value="eventday">
          <EventDayTab bookingId={id} participants={participants} attMap={attMap} performance={performance} canRecord={hasRole("ops", "admin", "provider")} reload={load} />
        </TabsContent>

        {/* CERTIFICATES */}
        <TabsContent value="certificates">
          <Card>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">Certificates</h3>
              {isOps && (
                <PrimaryButton data-testid="generate-certificates-btn" onClick={async () => {
                  try { const r = await api.post(`/bookings/${id}/certificates/generate`); toast.success(`${r.data.created} certificate(s) issued`); load(); }
                  catch (e) { toast.error(apiError(e)); }
                }}><Award className="h-4 w-4" /> Generate certificates</PrimaryButton>
              )}
            </div>
            {booking.status !== "completed" && <p className="mb-3 text-sm text-amber-400">Certificates can only be issued once the booking is marked <strong>completed</strong>.</p>}
            {certificates.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No certificates issued yet.</p> : (
              <div className="grid gap-3 sm:grid-cols-2">
                {certificates.map((c) => (
                  <div key={c.id} className="flex items-center justify-between rounded-xl border border-border bg-background/40 p-4">
                    <div>
                      <p className="font-medium">{c.participant_name}</p>
                      <p className="font-mono text-xs text-muted-foreground">{c.serial}</p>
                    </div>
                    <Link to={`/certificate/${c.id}`} target="_blank" className="rounded-lg bg-primary/15 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/25">View</Link>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {isFinance && booking.payment_status === "paid" && !booking.reconciled && (
        <Card className="mt-4 border-emerald-500/30">
          <div className="flex items-center justify-between">
            <div><p className="font-display text-lg font-semibold">Finance reconciliation</p><p className="text-sm text-muted-foreground">Close this booking after verifying the payment.</p></div>
            <PrimaryButton data-testid="close-booking-btn" onClick={async () => { try { await api.post(`/bookings/${id}/close`); toast.success("Booking closed & reconciled"); load(); } catch (e) { toast.error(apiError(e)); } }}>Close booking</PrimaryButton>
          </div>
        </Card>
      )}
      {booking.reconciled && <p className="mt-4 flex items-center gap-2 text-sm text-emerald-400"><CheckCircle2 className="h-4 w-4" /> Reconciled & closed by finance.</p>}
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-border/50 py-1.5 text-sm last:border-0">
    <span className="text-muted-foreground">{label}</span><span className="font-medium capitalize">{String(value ?? "-")}</span>
  </div>
);

// =================================================== Participants tab
function ParticipantsTab({ bookingId, participants, consentMap, canEdit, reload }) {
  const [open, setOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [form, setForm] = useState({ name: "", age: "", gender: "", grade: "", guardian_name: "", guardian_email: "", guardian_phone: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const add = async (e) => {
    e.preventDefault();
    try {
      const age = form.age ? Number(form.age) : null;
      await api.post(`/bookings/${bookingId}/participants`, { ...form, age, is_minor: age === null || age < 18 });
      toast.success("Participant added"); setOpen(false); setForm({ name: "", age: "", gender: "", grade: "", guardian_name: "", guardian_email: "", guardian_phone: "" }); reload();
    } catch (err) { toast.error(apiError(err)); }
  };
  const importCsv = async (e) => {
    e.preventDefault();
    try { const r = await api.post(`/bookings/${bookingId}/participants/import`, { csv_text: csv }); toast.success(`${r.data.created} imported`); if (r.data.errors?.length) toast.warning(`${r.data.errors.length} rows skipped`); setCsvOpen(false); setCsv(""); reload(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const requestConsent = async (pid) => {
    try { const r = await api.post(`/participants/${pid}/consent/request`); toast.success("Consent requested"); navigator.clipboard?.writeText(r.data.consent_link); toast.message("Consent link copied to clipboard"); reload(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const del = async (pid) => { try { await api.delete(`/participants/${pid}`); toast.success("Removed"); reload(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg font-semibold">Roster ({participants.length})</h3>
        {canEdit && (
          <div className="flex gap-2">
            <GhostButton data-testid="import-csv-btn" onClick={() => setCsvOpen(true)}><Upload className="h-4 w-4" /> Import CSV</GhostButton>
            <PrimaryButton data-testid="add-participant-btn" onClick={() => setOpen(true)}><UserPlus className="h-4 w-4" /> Add participant</PrimaryButton>
          </div>
        )}
      </div>
      {participants.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No participants yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left font-mono text-xs uppercase text-muted-foreground">
              <th className="px-3 py-2">Name</th><th className="px-3 py-2">Age</th><th className="px-3 py-2">Grade</th><th className="px-3 py-2">Guardian</th><th className="px-3 py-2">Consent</th>{canEdit && <th className="px-3 py-2"></th>}
            </tr></thead>
            <tbody>
              {participants.map((p) => {
                const consent = consentMap[p.id];
                return (
                  <tr key={p.id} data-testid={`participant-row-${p.id}`} className="border-b border-border/60 last:border-0">
                    <td className="px-3 py-2 font-medium">{p.name}{p.is_minor && <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 font-mono text-[10px] uppercase text-amber-300">minor</span>}</td>
                    <td className="px-3 py-2 text-muted-foreground">{p.age ?? "-"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{p.grade || "-"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{p.guardian_name || p.guardian_email || "-"}</td>
                    <td className="px-3 py-2"><StatusBadge status={p.consent_status === "not_requested" ? "not_requested" : (consent?.status || p.consent_status)} /></td>
                    {canEdit && (
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          {p.consent_status !== "consented" && (
                            <button data-testid={`request-consent-${p.id}`} onClick={() => requestConsent(p.id)} title="Request consent" className="rounded-lg bg-primary/15 p-1.5 text-primary hover:bg-primary/25"><Send className="h-4 w-4" /></button>
                          )}
                          <button onClick={() => del(p.id)} className="rounded-lg p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add participant" wide>
        <form onSubmit={add} className="grid gap-4 sm:grid-cols-2">
          <Input label="Full name" testId="part-name" required value={form.name} onChange={set("name")} className="sm:col-span-2" />
          <Input label="Age" testId="part-age" type="number" value={form.age} onChange={set("age")} />
          <Input label="Grade / Class" testId="part-grade" value={form.grade} onChange={set("grade")} />
          <Input label="Guardian name" testId="part-guardian-name" value={form.guardian_name} onChange={set("guardian_name")} />
          <Input label="Guardian email" testId="part-guardian-email" type="email" value={form.guardian_email} onChange={set("guardian_email")} />
          <Input label="Guardian phone" testId="part-guardian-phone" value={form.guardian_phone} onChange={set("guardian_phone")} className="sm:col-span-2" />
          <div className="flex justify-end gap-2 sm:col-span-2"><GhostButton type="button" onClick={() => setOpen(false)}>Cancel</GhostButton><PrimaryButton data-testid="save-participant-btn" type="submit">Add</PrimaryButton></div>
        </form>
      </Modal>

      <Modal open={csvOpen} onClose={() => setCsvOpen(false)} title="Import roster (CSV)" wide>
        <form onSubmit={importCsv}>
          <p className="mb-2 text-sm text-muted-foreground">Paste CSV with headers: <code className="text-primary">name,age,gender,grade,guardian_name,guardian_email,guardian_phone</code></p>
          <textarea data-testid="roster-import-csv-input" value={csv} onChange={(e) => setCsv(e.target.value)} rows={8} required
            className="w-full rounded-lg border border-input bg-background p-3 font-mono text-xs outline-none focus:border-primary"
            placeholder={"name,age,grade,guardian_email\nAarav Sharma,14,9,parent1@example.com\nDiya Patel,13,8,parent2@example.com"} />
          <div className="mt-3 flex justify-end gap-2"><GhostButton type="button" onClick={() => setCsvOpen(false)}>Cancel</GhostButton><PrimaryButton data-testid="confirm-csv-import-btn" type="submit">Import</PrimaryButton></div>
        </form>
      </Modal>
    </Card>
  );
}

// =================================================== Logistics tab
function LogisticsTab({ bookingId, booking, assignments, isOps, reload }) {
  const [providers, setProviders] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [aform, setAform] = useState({ provider_id: "", role: "transport", details: "" });
  const [itin, setItin] = useState(booking.itinerary?.length ? booking.itinerary : [{ day: "Day 1", time: "", title: "", location: "", notes: "" }]);

  useEffect(() => { if (isOps) api.get("/providers").then((r) => setProviders(r.data.filter((p) => p.status === "verified"))).catch(() => {}); }, [isOps]);

  const assign = async (e) => {
    e.preventDefault();
    if (!aform.provider_id) { toast.error("Select a provider"); return; }
    try { await api.post(`/bookings/${bookingId}/assignments`, aform); toast.success("Provider assigned"); setAssignOpen(false); reload(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const removeAssign = async (aid) => { try { await api.delete(`/assignments/${aid}`); reload(); } catch (e) { toast.error(apiError(e)); } };

  const setItinField = (i, k, v) => setItin((arr) => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const saveItin = async () => { try { await api.put(`/bookings/${bookingId}/itinerary`, { items: itin.filter((i) => i.title) }); toast.success("Itinerary saved"); reload(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><Truck className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-semibold">Assignments</h3></div>
          {isOps && <PrimaryButton data-testid="assign-provider-btn" onClick={() => setAssignOpen(true)}><Plus className="h-4 w-4" /> Assign</PrimaryButton>}
        </div>
        {assignments.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">No providers assigned.</p> : (
          <div className="space-y-2">
            {assignments.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-xl border border-border bg-background/40 p-3">
                <div><p className="font-medium">{a.provider_name}</p><p className="text-xs capitalize text-muted-foreground">{a.role}{a.details ? ` · ${a.details}` : ""}</p></div>
                <div className="flex items-center gap-2"><StatusBadge status={a.status} />{isOps && <button onClick={() => removeAssign(a.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="mb-4 flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-semibold">Itinerary</h3></div>
        {isOps ? (
          <div className="space-y-2">
            {itin.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <input placeholder="Day" value={it.day} onChange={(e) => setItinField(i, "day", e.target.value)} className="col-span-3 rounded-lg border border-input bg-background px-2 py-2 text-sm outline-none focus:border-primary" />
                <input placeholder="Time" value={it.time} onChange={(e) => setItinField(i, "time", e.target.value)} className="col-span-3 rounded-lg border border-input bg-background px-2 py-2 text-sm outline-none focus:border-primary" />
                <input placeholder="Activity" value={it.title} onChange={(e) => setItinField(i, "title", e.target.value)} className="col-span-6 rounded-lg border border-input bg-background px-2 py-2 text-sm outline-none focus:border-primary" />
                <input placeholder="Location" value={it.location} onChange={(e) => setItinField(i, "location", e.target.value)} className="col-span-12 rounded-lg border border-input bg-background px-2 py-2 text-sm outline-none focus:border-primary" />
              </div>
            ))}
            <button type="button" onClick={() => setItin((a) => [...a, { day: `Day ${a.length + 1}`, time: "", title: "", location: "", notes: "" }])} className="text-sm text-primary hover:underline">+ Add item</button>
            <PrimaryButton data-testid="save-itinerary-btn" onClick={saveItin} className="mt-2 w-full">Save itinerary</PrimaryButton>
          </div>
        ) : (
          booking.itinerary?.length ? booking.itinerary.map((it, i) => (
            <div key={i} className="border-b border-border/50 py-2 text-sm last:border-0"><span className="font-mono text-xs text-primary">{it.day} {it.time}</span> - {it.title} {it.location && <span className="text-muted-foreground">({it.location})</span>}</div>
          )) : <p className="py-4 text-center text-sm text-muted-foreground">Itinerary not published yet.</p>
        )}
      </Card>

      <Modal open={assignOpen} onClose={() => setAssignOpen(false)} title="Assign provider">
        <form onSubmit={assign} className="space-y-4">
          <Select label="Provider" testId="assign-provider-select" value={aform.provider_id} onChange={(e) => setAform((f) => ({ ...f, provider_id: e.target.value }))}>
            <option value="">Select verified provider</option>
            {providers.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.category})</option>)}
          </Select>
          <Select label="Role" testId="assign-role-select" value={aform.role} onChange={(e) => setAform((f) => ({ ...f, role: e.target.value }))}>
            <option value="transport">Transport</option><option value="venue">Venue</option><option value="coach">Coach</option><option value="accommodation">Accommodation</option><option value="catering">Catering</option>
          </Select>
          <Textarea label="Details" testId="assign-details" rows={2} value={aform.details} onChange={(e) => setAform((f) => ({ ...f, details: e.target.value }))} />
          <div className="flex justify-end gap-2"><GhostButton type="button" onClick={() => setAssignOpen(false)}>Cancel</GhostButton><PrimaryButton data-testid="confirm-assign-btn" type="submit">Assign</PrimaryButton></div>
        </form>
      </Modal>
    </div>
  );
}

// =================================================== Event Day tab
function EventDayTab({ bookingId, participants, attMap, performance, canRecord, reload }) {
  const [local, setLocal] = useState(attMap);
  const [perfOpen, setPerfOpen] = useState(false);
  const [pf, setPf] = useState({ participant_id: "", metric: "", value: "", position: "", remarks: "" });

  const mark = (pid, status) => setLocal((m) => ({ ...m, [pid]: status }));
  const saveAttendance = async () => {
    try { const records = participants.map((p) => ({ participant_id: p.id, status: local[p.id] || "absent" })); await api.post(`/bookings/${bookingId}/attendance`, { records }); toast.success("Attendance saved"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const savePerf = async (e) => {
    e.preventDefault();
    try { await api.post(`/bookings/${bookingId}/performance`, pf); toast.success("Result recorded"); setPerfOpen(false); setPf({ participant_id: "", metric: "", value: "", position: "", remarks: "" }); reload(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const nameOf = (pid) => participants.find((p) => p.id === pid)?.name || "-";

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-semibold">Attendance</h3></div>
          {canRecord && participants.length > 0 && <PrimaryButton data-testid="save-attendance-btn" onClick={saveAttendance}>Save</PrimaryButton>}
        </div>
        {participants.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">Add participants first.</p> : (
          <div className="space-y-2">
            {participants.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-2">
                <span className="text-sm font-medium">{p.name}</span>
                {canRecord ? (
                  <div className="flex gap-1">
                    <button data-testid={`attend-present-${p.id}`} onClick={() => mark(p.id, "present")} className={`rounded-md px-3 py-1 text-xs font-medium ${(local[p.id] || attMap[p.id]) === "present" ? "bg-emerald-500 text-white" : "bg-secondary text-muted-foreground"}`}>Present</button>
                    <button data-testid={`attend-absent-${p.id}`} onClick={() => mark(p.id, "absent")} className={`rounded-md px-3 py-1 text-xs font-medium ${(local[p.id] || attMap[p.id]) === "absent" ? "bg-rose-500 text-white" : "bg-secondary text-muted-foreground"}`}>Absent</button>
                  </div>
                ) : <StatusBadge status={attMap[p.id] || "pending"} />}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-semibold">Scores & results</h3></div>
          {canRecord && participants.length > 0 && <PrimaryButton data-testid="add-performance-btn" onClick={() => setPerfOpen(true)}><Plus className="h-4 w-4" /> Record</PrimaryButton>}
        </div>
        {performance.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">No results recorded.</p> : (
          <div className="space-y-2">
            {performance.map((r) => (
              <div key={r.id} className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm">
                <div className="flex justify-between"><span className="font-medium">{nameOf(r.participant_id)}</span>{r.position && <span className="rounded bg-primary/15 px-2 py-0.5 font-mono text-xs text-primary">{r.position}</span>}</div>
                <p className="text-xs text-muted-foreground">{r.metric}: <span className="font-mono">{r.value}</span>{r.remarks ? ` · ${r.remarks}` : ""}</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal open={perfOpen} onClose={() => setPerfOpen(false)} title="Record result">
        <form onSubmit={savePerf} className="space-y-4">
          <Select label="Participant" testId="perf-participant" value={pf.participant_id} onChange={(e) => setPf((f) => ({ ...f, participant_id: e.target.value }))} required>
            <option value="">Select participant</option>{participants.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Input label="Metric (e.g. 100m sprint)" testId="perf-metric" required value={pf.metric} onChange={(e) => setPf((f) => ({ ...f, metric: e.target.value }))} />
          <Input label="Value (e.g. 12.4s)" testId="perf-value" required value={pf.value} onChange={(e) => setPf((f) => ({ ...f, value: e.target.value }))} />
          <Input label="Position (optional)" testId="perf-position" value={pf.position} onChange={(e) => setPf((f) => ({ ...f, position: e.target.value }))} placeholder="e.g. 1st" />
          <Textarea label="Remarks" testId="perf-remarks" rows={2} value={pf.remarks} onChange={(e) => setPf((f) => ({ ...f, remarks: e.target.value }))} />
          <div className="flex justify-end gap-2"><GhostButton type="button" onClick={() => setPerfOpen(false)}>Cancel</GhostButton><PrimaryButton data-testid="save-performance-btn" type="submit">Save</PrimaryButton></div>
        </form>
      </Modal>
    </div>
  );
}
